import state from './apps-state.js';
import * as render from './apps-render.js';
import * as nav from './apps-navigation.js';
import * as api from './apps-api.js';
import * as events from './apps-events.js';
import { updateSliderVisibility } from './apps-slider.js';
import * as keyboard from './apps-kb-main.js';

export function openFolderView(folder) {
    state.openFolder = folder;
    state.currentFolderScreen = 0;
    const folderIcons = window.icons.filter(icon => icon.kfolder === folder.id);
    
    // Fade out main appspace
    document.querySelector('.appspace-container').classList.add('folder-open');
    
    document.getElementById('folderTitle').textContent = folder.title;
    document.getElementById('folderOverlay').classList.add('open');
    
    // Get layout to match main screen dimensions
    const layout = render.calculateLayout();
    
    // Calculate folder dimensions based on max grand rank
    let maxGrandRank = 0;
    folderIcons.forEach(icon => {
        const grandRank = Math.floor(icon.position / 4);
        if (grandRank > maxGrandRank) maxGrandRank = grandRank;
    });
    
    const dimensions = window.calculateFolderDimensions(layout, maxGrandRank);
    const folderWidth = dimensions.width; // No padding adjustment needed
    
    const screenGap = layout.regionGapH * 3;
    
    // Calculate layout for folder (single band)
    const screens = render.organizeIntoScreens(folderIcons, true);
    
    // Render screens
    const screensContainer = document.getElementById('folderScreens');
    screensContainer.innerHTML = '';
    screensContainer.style.gap = screenGap + 'px';
    
    screens.forEach((screen, _index) => {
        const screenDiv = document.createElement('div');
        screenDiv.className = 'folder-screen';
        
        // Set explicit width to match main screen
        screenDiv.style.width = folderWidth + 'px';
        screenDiv.style.minWidth = folderWidth + 'px';
        screenDiv.style.flexBasis = folderWidth + 'px';
        
        screenDiv.innerHTML = render.renderScreen(screen, true);
        
        screensContainer.appendChild(screenDiv);
    });
    
    // CRITICAL FIX: Move scroll zones and indicators from folder-content to folder-container
    const folderContainer = document.getElementById('folderContainer');
    const folderContent = document.querySelector('.folder-content');

    // Move indicators to folder-container to prevent clipping by overflow:hidden
    const insertIndicator = document.getElementById('folderInsertIndicator');
    const dropPreview = document.getElementById('folderDropPreview');
    
    if (insertIndicator && insertIndicator.parentElement !== folderContainer) {
        folderContainer.appendChild(insertIndicator);
    }
    if (dropPreview && dropPreview.parentElement !== folderContainer) {
        folderContainer.appendChild(dropPreview);
    }
    
    // Get the scroll zone elements (they exist in HTML inside folder-content)
    let leftZone = document.getElementById('folderScrollZoneLeft');
    let rightZone = document.getElementById('folderScrollZoneRight');
    
    // Move them to folder-container (appendChild will move them, not duplicate)
    if (leftZone) {
        console.log('Moving left scroll zone to folder-container');
        folderContainer.appendChild(leftZone);
    } else {
        // Create if doesn't exist (shouldn't happen with your HTML)
        leftZone = document.createElement('div');
        leftZone.id = 'folderScrollZoneLeft';
        leftZone.className = 'scroll-zone left';
        folderContainer.appendChild(leftZone);
        console.log('Created new left scroll zone');
    }
    
    if (rightZone) {
        console.log('Moving right scroll zone to folder-container');
        folderContainer.appendChild(rightZone);
    } else {
        // Create if doesn't exist (shouldn't happen with your HTML)
        rightZone = document.createElement('div');
        rightZone.id = 'folderScrollZoneRight';
        rightZone.className = 'scroll-zone right';
        folderContainer.appendChild(rightZone);
        console.log('Created new right scroll zone');
    }
    
    console.log('Folder container dimensions:', folderContainer.style.width, 'x', folderContainer.style.height);
    console.log('Folder content dimensions:', folderContent.style.width, 'x', folderContent.style.height);
    
    render.renderPageIndicators(screens.length, 'folderPageIndicators');
    
    // Navigate to first non-empty screen
    const firstNonEmpty = findFirstNonEmptyFolderScreen(screens.length, folderIcons);
    nav.goToFolderScreen(firstNonEmpty);
    
    render.updateFolderHeight();
    
    attachFolderEventListeners();
    setupFolderTitleEditing();

    keyboard.onFolderOpen();
}

export function attachFolderEventListeners() {
    const folderContent = document.querySelector('.folder-content');

    // Remove any existing handlers first
    folderContent.removeEventListener('mousedown', handleFolderDragStart);
    folderContent.removeEventListener('touchstart', handleFolderDragStart);
    
    // Re-attach
    folderContent.addEventListener('mousedown', handleFolderDragStart);
    folderContent.addEventListener('touchstart', handleFolderDragStart, { passive: true });
    
    // Icon event listeners
    events.attachIconEventListeners('#folderScreens');
}

// Define handlers at module level (after attachFolderEventListeners)
export function handleFolderDragStart(e) {
    if (e.target.closest('.appicon') || state.isDraggingIcon) return;
    const touch = e.touches ? e.touches[0] : e;
    const screensContainer = document.getElementById('folderScreens');
    
    state.folderDragState.isDragging = true;
    state.folderDragState.startX = touch.clientX;
    state.folderDragState.currentX = touch.clientX;
    state.folderDragState.startScreen = state.currentFolderScreen;
    screensContainer.classList.add('dragging');
    
    // Hide virtual cursor, scroll zones, modal zones, and drop indicators when folder screen drag starts
    import('./apps-kb-cursor.js').then(cursor => {
        if (cursor && typeof cursor.hideVirtualCursor === 'function') {
            cursor.hideVirtualCursor();
        }
    });
    
    import('./apps-kb-scroll.js').then(scroll => {
        if (scroll && typeof scroll.deactivateScrollZones === 'function') {
            scroll.deactivateScrollZones();
        }
    });
    
    import('./apps-kb-modal.js').then(modal => {
        if (modal && typeof modal.unhighlightModalZone === 'function') {
            modal.unhighlightModalZone('top');
            modal.unhighlightModalZone('bottom');
        }
    });
    
    import('./apps-drag.js').then(drag => {
        if (drag && typeof drag.clearDropIndicators === 'function') {
            drag.clearDropIndicators();
        }
    });
}

export function handleFolderDragMove(e) {
    if (!state.folderDragState.isDragging || state.isDraggingIcon) return;
    const touch = e.touches ? e.touches[0] : e;
    const screensContainer = document.getElementById('folderScreens');
    
    state.folderDragState.currentX = touch.clientX;
    const diff = state.folderDragState.currentX - state.folderDragState.startX;
    
    // Use same pixel-based calculation as goToFolderScreen
    const layout = render.calculateLayout();
    const screenGap = layout.regionGapH * 2; // Match the gap from openFolderView
    
    // Get actual screen width from DOM
    const firstScreen = screensContainer.children[0];
    const calculatedScreenWidth = firstScreen ? firstScreen.offsetWidth : 
        (layout.regionWidth * layout.leaves + (layout.regionGapH * (layout.leaves - 1)) + (2 * layout.margin));
    
    // Calculate offset: start from the current screen position, then add drag difference
    const offset = (-state.folderDragState.startScreen * (calculatedScreenWidth + screenGap)) + diff;
    screensContainer.style.transform = `translateX(${offset}px)`;
}

export function handleFolderDragEnd(_e) {
    if (!state.folderDragState.isDragging || state.isDraggingIcon) return;
    const screensContainer = document.getElementById('folderScreens');
    
    state.folderDragState.isDragging = false;
    screensContainer.classList.remove('dragging');
    
    const diff = state.folderDragState.currentX - state.folderDragState.startX;
    const folderContent = document.querySelector('.folder-content');
    const screenWidth = folderContent.clientWidth;
    const threshold = screenWidth * 0.2;
    
    if (diff < -threshold && state.folderDragState.startScreen < screensContainer.children.length - 1) {
        nav.goToFolderScreen(state.folderDragState.startScreen + 1);
    } else if (diff > threshold && state.folderDragState.startScreen > 0) {
        nav.goToFolderScreen(state.folderDragState.startScreen - 1);
    } else {
        nav.goToFolderScreen(state.folderDragState.startScreen);
    }
}

// Attach global handlers once
document.addEventListener('mousemove', handleFolderDragMove);
document.addEventListener('touchmove', handleFolderDragMove, { passive: false });
document.addEventListener('mouseup', handleFolderDragEnd);
document.addEventListener('touchend', handleFolderDragEnd);

export function setupFolderTitleEditing() {
    const titleEl = document.getElementById('folderTitle');
    
    // Make it focusable
    titleEl.setAttribute('tabindex', '0');
    
    titleEl.onclick = (e) => {
        e.stopPropagation();

        if (titleEl.classList.contains('editing')) {
            return;
        }

        titleEl.contentEditable = 'true';
        titleEl.classList.add('editing');
        titleEl.focus();
        
        const range = document.createRange();
        range.selectNodeContents(titleEl);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    };

    titleEl.onblur = () => {
        // Only update and re-render if we were actually editing
        if (titleEl.classList.contains('editing')) {
            titleEl.contentEditable = 'false';
            titleEl.classList.remove('editing');
            
            state.openFolder.title = titleEl.textContent.trim();
            api.updateIcon(state.openFolder);
            render.renderLaunchpad();
        }
    };

    titleEl.onkeydown = (e) => {
        // If editing, only handle Enter to exit editing
        if (titleEl.classList.contains('editing')) {
            if (e.key === 'Enter') {
                e.preventDefault();
                titleEl.blur();
            }
            return;
        }

        // Navigation when just focused (not editing)
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            
            // Close folder and position cursor on folder icon
            const closedFolderId = state.openFolder.id;
            closeFolderView();
            
            // Position cursor on the folder icon in main screen
            import('./apps-kb-navigation.js').then(navigation => {
                if (navigation.handleFolderClose) {
                    navigation.handleFolderClose(closedFolderId);
                }
            });
            return;
        }

        if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            // Set keyboard state BEFORE blur to prevent renderLaunchpad from resetting it
            import('./apps-kb-state.js').then(kbState => {
                import('./apps-kb-modal.js').then(modal => {
                    const keyboardState = kbState.keyboardState;
                    keyboardState.virtualCursor.active = true;
                    keyboardState.virtualCursor.isInModalZone = true;
                    keyboardState.virtualCursor.modalZonePosition = 'top';
                    keyboardState.virtualCursor.rank = -1;
                    // keyboardState.virtualCursor.file = 0;
                    
                    // NOW blur, which will trigger renderLaunchpad but keyboard state is already set
                    titleEl.blur();
                    modal.highlightModalZone('top');
                });
            });
        } else if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            titleEl.blur(); // Blur before returning to folder view
            // Return to top rank of folder, preserving file position
            import('./apps-kb-helpers.js').then(helpers => {
                import('./apps-kb-cursor.js').then(cursor => {
                    import('./apps-render.js').then(render => {
                        import('./apps-kb-state.js').then(kbState => {
                            const keyboardState = kbState.keyboardState;
                            const layout = render.calculateLayout();
                            const icons = window.icons.filter(icon => icon.kfolder === state.openFolder.id);
                            const minRank = helpers.getMinOccupiedRank(icons, layout, true);
                            
                            // Use the preserved file position
                            const file = keyboardState.virtualCursor.file;
                            
                            if (minRank >= 0) {
                                cursor.showVirtualCursor(minRank, file);
                            }
                        });
                    });
                });
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            // Start editing
            titleEl.contentEditable = 'true';
            titleEl.classList.add('editing');
            
            const range = document.createRange();
            range.selectNodeContents(titleEl);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };
}

export function closeFolderView() {
    if (!state.openFolder) return;
    
    // Store folder ID before clearing for keyboard navigation
    const closedFolderId = state.openFolder.id;
    
    updateSliderVisibility(true);
    document.getElementById('folderOverlay').classList.remove('open');
    document.querySelector('.appspace-container').classList.remove('folder-open');
    state.openFolder = null;
    state.currentFolderScreen = 0;
    
    // Clear modal zones when folder closes
    import('./apps-kb-modal.js').then(modal => {
        modal.clearAllModalZones();
    });
    
    keyboard.onFolderClose(closedFolderId);
}

export function findFirstNonEmptyFolderScreen(totalScreens, folderIcons) {
    const layout = render.calculateLayout();
    const regionsPerScreen = layout.leaves;
    
    for (let screenIndex = 0; screenIndex < totalScreens; screenIndex++) {
        const screenStartRegion = screenIndex * regionsPerScreen;
        const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
        
        const iconsInScreen = folderIcons.filter(icon => 
            icon.region >= screenStartRegion && icon.region <= screenEndRegion
        );
        
        if (iconsInScreen.length > 0) {
            return screenIndex;
        }
    }
    
    return 0; // Default to first screen if all empty
}
